import { useState, useCallback, useEffect, useRef } from 'react';
import subscriptionService from '../services/SubscriptionService';
import analytics from '../services/analytics';

// While entitlement is UNDETERMINED (slow/unreachable backend) we let the user
// browse and re-check in the background until we get an authoritative answer.
const RECONCILE_INTERVAL_MS = 5000;
const RECONCILE_MAX_ATTEMPTS = 24; // ~2 minutes of active polling while undetermined

export function useSubscription({ checkOnMount = false } = {}) {
  const [products, setProducts] = useState([]);
  // null = unknown/undetermined; true/false = authoritative answer.
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [determined, setDetermined] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(checkOnMount);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState(null);
  const reconcileAttemptsRef = useRef(0);

  // `background` re-checks (the reconciler) must not toggle the foreground
  // spinner or the user would see a flicker while browsing.
  const checkSubscription = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setIsCheckingSubscription(true);
      setError(null);
    }
    try {
      const { subscribed, determined: isDetermined } = await subscriptionService.getEntitlement();
      setDetermined(isDetermined);
      setHasActiveSubscription(isDetermined ? subscribed : null);
      // Enrich every analytics event with the resolved tier (once authoritative).
      if (isDetermined) {
        analytics.setUserContext({ subscription_tier: subscribed ? 'subscriber' : 'free' });
      }
      return isDetermined ? subscribed : null;
    } catch (err) {
      // getEntitlement is defensive and shouldn't throw, but treat any leak as
      // undetermined (never as "not subscribed").
      console.error('useSubscription: Error checking subscription status:', err);
      setDetermined(false);
      setHasActiveSubscription(null);
      return null;
    } finally {
      if (!background) {
        setIsCheckingSubscription(false);
      }
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    setIsLoadingProducts(true);
    setError(null);
    try {
      const { products: fetchedProducts } = await subscriptionService.getSubscriptionSummary();
      setProducts(fetchedProducts);
      return fetchedProducts;
    } catch (err) {
      console.error('useSubscription: Error loading subscription plans:', err);
      setError("We couldn't load subscription plans right now. Please try again.");
      return [];
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    if (checkOnMount) {
      checkSubscription();
    }
  }, [checkOnMount, checkSubscription]);

  // Background reconciler: while we still don't have an authoritative answer,
  // keep re-checking (bounded) and also when the tab becomes visible again. Once
  // `determined` flips true this stops; the SubscriptionGate then redirects
  // declaratively (enforce paywall / send a subscriber home) with no flash.
  useEffect(() => {
    if (!checkOnMount || determined) {
      reconcileAttemptsRef.current = 0;
      return undefined;
    }

    let cancelled = false;
    const timer = setInterval(() => {
      if (reconcileAttemptsRef.current >= RECONCILE_MAX_ATTEMPTS) {
        clearInterval(timer);
        return;
      }
      reconcileAttemptsRef.current += 1;
      if (!cancelled) {
        checkSubscription({ background: true });
      }
    }, RECONCILE_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        checkSubscription({ background: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkOnMount, determined, checkSubscription]);

  return {
    products,
    hasActiveSubscription,
    determined,
    isCheckingSubscription,
    isLoadingProducts,
    error,
    checkSubscription,
    fetchProducts,
    clearError: () => setError(null),
  };
}
